"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCommandBar } from "@/hooks/use-command-bar";
import { useEntitiesFromDb } from "@/hooks/schema/use-entities-from-db";
import { useCommandBarPages } from "@/hooks/use-command-bar-pages";
import { useEntityInstanceModalStack } from "@/stores/entity-instance-modal-stack-store";
import { fuzzySort } from "@/lib/fuzzy-search";
import { useEffect, useMemo } from "react";

interface CommandResult {
  /** React key, unique across all groups. */
  key: string;
  label: string;
  /** Navigation target (subdomain-less; the proxy prefixes the subdomain). */
  href: string;
}

export function CommandBar() {
  const router = useRouter();
  const { isOpen, setIsOpen } = useCommandBar();
  const resetModalStack = useEntityInstanceModalStack((state) => state.reset);
  const [query, setQuery] = React.useState("");
  const { availableEntities } = useEntitiesFromDb();
  const pages = useCommandBarPages();
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Each group is ranked independently against the query.
  const entityResults = useMemo<CommandResult[]>(
    () =>
      fuzzySort(availableEntities ?? [], query, (item) => item.name).map((item) => ({
        key: item.id,
        label: item.name,
        href: `/${item.name}`,
      })),
    [availableEntities, query],
  );

  const pageResults = useMemo<CommandResult[]>(
    () =>
      fuzzySort(pages, query, (item) => item.title).map((item) => ({
        key: item.id,
        label: item.title,
        href: item.href,
      })),
    [pages, query],
  );

  // Flat, ordered list used for keyboard navigation + selection. Order must
  // match the render order below (entities first, then settings/pages).
  const flatResults = useMemo<CommandResult[]>(
    () => [...entityResults, ...pageResults],
    [entityResults, pageResults],
  );

  // Handle item selection
  const handleSelect = React.useCallback(
    (href: string) => {
      resetModalStack();
      router.push(href);
      setIsOpen(false);
    },
    [resetModalStack, router, setIsOpen],
  );

  // Reset query and selection when the dialog opens. Resetting on close would
  // change the search term while the dialog animates out, triggering a refetch
  // that flickers the results (and the dialog) right as you select an item.
  React.useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
      } else if (e.key === "Enter" && flatResults.length > 0) {
        e.preventDefault();
        handleSelect(flatResults[selectedIndex].href);
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [flatResults, selectedIndex, handleSelect, setIsOpen],
  );

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatResults]);

  const renderRow = (result: CommandResult, index: number) => (
    <Link
      key={result.key}
      href={result.href}
      className={`block cursor-pointer px-5 py-2 text-sm ${
        index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
      }`}
      onClick={(e) => {
        if (!e.metaKey && !e.ctrlKey) {
          resetModalStack();
          setIsOpen(false);
        }
      }}
    >
      <div className="flex items-center">
        <span>{result.label}</span>
      </div>
    </Link>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="mb-auto mt-[18vh] h-[400px] max-w-[640px] overflow-hidden p-0 shadow-lg">
        <DialogTitle className="sr-only">Search</DialogTitle>

        <div className="flex h-full w-full flex-col items-start rounded-lg border bg-background shadow-lg">
          <div className="w-full flex-shrink-0 border-b px-3 py-2">
            <Input
              placeholder="Search entities, settings, pages..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex h-11 w-full rounded-md border-none bg-transparent py-3 text-sm shadow-none outline-none placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              autoFocus
            />
          </div>

          <div className="min-h-0 w-full grow overflow-y-auto">
            {flatResults.length > 0 ? (
              <div className="py-2">
                {entityResults.length > 0 && (
                  <>
                    <div className="px-5 pb-1.5 pt-2 text-xs font-medium text-muted-foreground">
                      Entities
                    </div>
                    {entityResults.map((result, i) => renderRow(result, i))}
                  </>
                )}
                {pageResults.length > 0 && (
                  <>
                    <div className="px-5 pb-1.5 pt-2 text-xs font-medium text-muted-foreground">
                      Settings / Pages
                    </div>
                    {pageResults.map((result, i) => renderRow(result, entityResults.length + i))}
                  </>
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">No results found</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
